import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Button,
  Space,
  message,
  Typography,
  LinkOutlined,
} from "@mimber/ui-web"
import { Form, InputField } from "@mimber/forms"
import { useUpdateOrganizationProfile } from "@mimber/client/mimber-organization"
import type { OrganizationSocialsFormProps } from "./OrganizationSocialsForm.types"
import {
  organizationSocialsSchema,
  type OrganizationSocialsFormData,
} from "./OrganizationSocialsForm.schema"

const { Paragraph } = Typography

export const OrganizationSocialsForm: React.FunctionComponent<
  OrganizationSocialsFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)

  const form = useForm<OrganizationSocialsFormData>({
    defaultValues: {
      socialMediaFacebook: organization.socialMediaFacebook || "",
      socialMediaTwitter: organization.socialMediaTwitter || "",
      socialMediaLinkedin: organization.socialMediaLinkedin || "",
      socialMediaInstagram: organization.socialMediaInstagram || "",
    },
    resolver: zodResolver(organizationSocialsSchema),
    mode: "onBlur",
  })

  const {
    formState: { isDirty },
    reset,
  } = form

  const onSubmit = (data: OrganizationSocialsFormData) => {
    const payload = {
      socialMediaFacebook: data.socialMediaFacebook || null,
      socialMediaTwitter: data.socialMediaTwitter || null,
      socialMediaLinkedin: data.socialMediaLinkedin || null,
      socialMediaInstagram: data.socialMediaInstagram || null,
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        message.success("Social media links updated successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to update social media links")
      },
    })
  }

  const isLoading = updateProfile.isPending

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={disabled || isLoading}
      layout="vertical"
    >
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Add links to your organization's social media profiles. These will be
        displayed on your public profile.
      </Paragraph>

      <InputField
        name="socialMediaFacebook"
        label="Facebook"
        placeholder="https://facebook.com/your-organization"
        prefix={<LinkOutlined />}
      />
      <InputField
        name="socialMediaTwitter"
        label="Twitter / X"
        placeholder="https://twitter.com/your-organization"
        prefix={<LinkOutlined />}
      />
      <InputField
        name="socialMediaLinkedin"
        label="LinkedIn"
        placeholder="https://linkedin.com/company/your-organization"
        prefix={<LinkOutlined />}
      />
      <InputField
        name="socialMediaInstagram"
        label="Instagram"
        placeholder="https://instagram.com/your-organization"
        prefix={<LinkOutlined />}
      />

      <Space style={{ marginTop: 24 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={disabled || !isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>

        <Button
          onClick={() => reset()}
          disabled={disabled || !isDirty || isLoading}
        >
          Reset
        </Button>
      </Space>
    </Form>
  )
}
